import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useCan } from "@/lib/auth/useCan";

import { fmtCur } from "../../../renewals/format";

import { RemoveSubAccountDialog } from "./RemoveSubAccountDialog";
import { SubAccountForm } from "./SubAccountForm";

type SubLink = {
  link_id: string | null;
  sub_qb_id: number;
  effective_period: string | null;
  name: string;
  is_orphan: boolean;
  last_period: string | null;
  last_sub_mrr: number;
};

type PartnerLite = { qb_id: number; account_id: string; account_name: string };

/**
 * Sub-accounts management section on the expanded partner view. Admins can
 * add new sub-account links (dual-mode picker covers existing accounts +
 * billing-only orphan QB IDs) and remove existing ones. Renders even when
 * the list is empty — the section IS the affordance for setting one up.
 *
 * Backend already invalidates the partners cache on subcustomer_of create/
 * delete, so the partner ARR rollup updates without manual cache busting.
 */
export function SubAccountsSection({
  partner,
  subLinks,
}: {
  partner: PartnerLite;
  subLinks: SubLink[];
}) {
  const canAdmin = useCan("renewals.admin");
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<
    { link_id: string; name: string; sub_qb_id: number } | null
  >(null);

  const excludeQbs = subLinks.map((l) => l.sub_qb_id);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle>Sub-accounts ({subLinks.length})</CardTitle>
          <CardDescription>
            Sub-accounts contribute their subscription billing to this partner's annualized and
            run-rate totals from each link's effective period forward. Both existing accounts
            and billing-only QB IDs can be attached.
          </CardDescription>
        </div>
        {canAdmin && !adding && (
          <Button size="sm" onClick={() => setAdding(true)}>
            + Add sub-account
          </Button>
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {adding && (
          <SubAccountForm
            partner={partner}
            excludeQbs={excludeQbs}
            onSuccess={() => setAdding(false)}
            onCancel={() => setAdding(false)}
          />
        )}
        {subLinks.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No sub-accounts linked yet.
          </p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Sub-account</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2 text-right">Last sub MRR</th>
                  <th className="px-2 py-2">Effective from</th>
                  {canAdmin && <th className="px-2 py-2" style={{ width: 80 }} />}
                </tr>
              </thead>
              <tbody>
                {subLinks.map((s) => (
                  <tr key={s.sub_qb_id} className="border-t border-border">
                    <td className="px-2 py-1.5">
                      <span className="font-medium">{s.name}</span>
                      <span className="ml-2 font-mono text-xs text-muted-foreground">
                        QB-{s.sub_qb_id}
                      </span>
                    </td>
                    <td className="px-2 py-1.5">
                      {s.is_orphan ? (
                        <Badge variant="warning">Billing-only</Badge>
                      ) : (
                        <Badge variant="neutral">Account</Badge>
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums">
                      {s.last_sub_mrr > 0 ? (
                        <>
                          {fmtCur(s.last_sub_mrr)}
                          {s.last_period && (
                            <span className="text-muted-foreground"> · {s.last_period}</span>
                          )}
                        </>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="px-2 py-1.5 text-muted-foreground">
                      {s.effective_period ?? "—"}
                    </td>
                    {canAdmin && (
                      <td className="px-2 py-1.5">
                        {s.link_id && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setRemoving({
                                link_id: s.link_id!,
                                name: s.name,
                                sub_qb_id: s.sub_qb_id,
                              })
                            }
                          >
                            Remove
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      <RemoveSubAccountDialog
        link={removing}
        open={!!removing}
        onOpenChange={(v) => !v && setRemoving(null)}
      />
    </Card>
  );
}
